import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, EMPTY, Observable, ReplaySubject, firstValueFrom, from, of, switchMap, tap } from 'rxjs';
import { filter, map, shareReplay, take, timeout } from 'rxjs/operators';
import { SecureLocalStorageService } from 'shared';

import { getRedirectUrl } from '../../../provider';
import { HeaderConfig } from '../../models';
import { HeaderSettingsService } from './header-settings.service';

export const TOKEN_EXPIRY_OFFSET_MS = 10000; // 10 seconds
export const IDP_HINT_PARAM = 'kc_idp_hint';
export const IDP_HINT_KEY = 'IDP_HINT';
export const TOKEN_REFRESH_TIMEOUT_MS = 10000; // 10 seconds

export type RefreshStatus =
    | { status: 'IDLE' }
    | { status: 'TOKEN_STILL_VALID'; accessToken: string }
    | { status: 'REFRESHING' }
    | { status: 'SUCCESS'; accessToken: string }
    | { status: 'FAILED'; error: unknown };

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    public config: ReplaySubject<HeaderConfig> = new ReplaySubject<HeaderConfig>(1);

    private isInitialized = false;
    private authDomainWhitelist: Array<string> = [];
    private loggedIn$: Observable<boolean> | null = null;

    private readonly isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));
    private readonly headerSettings = inject(HeaderSettingsService);
    private readonly oauthService = inject(OAuthService);
    private readonly localStorageService = inject(SecureLocalStorageService);
    private readonly refreshStatus = new BehaviorSubject<RefreshStatus>({ status: 'IDLE' });

    public readonly onInitialized = new ReplaySubject<boolean>(1);

    public init(): Observable<void> {
        this.persistIdpHintFromUrl();

        if (this.isInitialized) {
            console.warn(`Cannot initialize Auth as it's already initialized`);
            return EMPTY;
        }

        return this.headerSettings.settings.pipe(take(1)).pipe(
            switchMap((config) => {
                const mergedConfig: HeaderConfig = {
                    ...{ redirectUri: getRedirectUrl() },
                    ...config,
                    jwtDomainWhitelist: config?.jwtDomainWhitelist ?? [],
                };
                this.authDomainWhitelist = mergedConfig.jwtDomainWhitelist;

                this.config.next(cloneDeep(mergedConfig));

                this.oauthService.configure(authCodeFlowConfig(mergedConfig));
                this.oauthService.setupAutomaticSilentRefresh({
                    timeoutFactor: 0.6,
                });

                return from(this.oauthService.loadDiscoveryDocument()).pipe(
                    tap(() => {
                        this.onInitialized.next(true);
                        this.isInitialized = true;
                    }),
                    map((): void => void 0)
                );
            })
        );
    }

    public checkAuthenticatedOrLogin(): Observable<boolean> {
        if (this.loggedIn$) {
            return this.loggedIn$;
        }

        this.loggedIn$ = this.init().pipe(
            switchMap(() => this.validateLogin()),
            map(() => this.isAuthenticated()),
            tap((isAuthenticated) => {
                if (!isAuthenticated) {
                    if (this.isBrowser) {
                        console.debug('User is not authenticated, trying to redirect to login');
                        this.login();
                    } else {
                        console.debug('User is not authenticated, but page is server rendered');
                    }
                }
            }),
            shareReplay(1)
        );

        return this.loggedIn$;
    }

    public login(redirectUrl?: string): void {
        this.validateInit();
        this.oauthService.initLoginFlow(redirectUrl, this.getAuthorizationParams());
    }

    public async refreshTokenIfExpired(): Promise<RefreshStatus> {
        // If we're already refreshing, wait for the result to avoid concurrent refreshes
        if (this.refreshStatus.value.status === 'REFRESHING') {
            return firstValueFrom(this.refreshStatus.pipe(filter((status) => status.status !== 'REFRESHING')));
        }

        // If our access token is still valid, return the current token.
        if (
            this.oauthService.hasValidAccessToken() &&
            Date.now() < this.oauthService.getAccessTokenExpiration() - TOKEN_EXPIRY_OFFSET_MS
        ) {
            return { status: 'TOKEN_STILL_VALID', accessToken: this.oauthService.getAccessToken() };
        }

        return this.refreshToken();
    }

    public async refreshToken(): Promise<RefreshStatus> {
        this.validateInit();

        const refreshToken = this.oauthService.getRefreshToken();
        if (!refreshToken) {
            const status: RefreshStatus = { status: 'FAILED', error: 'No refresh token available' };
            this.refreshStatus.next(status);
            return status;
        }

        try {
            console.debug('[AuthService] Attempting to refresh token');
            this.refreshStatus.next({ status: 'REFRESHING' });
            await firstValueFrom(from(this.oauthService.refreshToken()).pipe(timeout(TOKEN_REFRESH_TIMEOUT_MS)));

            const status: RefreshStatus = this.oauthService.hasValidAccessToken()
                ? { status: 'SUCCESS', accessToken: this.oauthService.getAccessToken() }
                : { status: 'FAILED', error: 'Token refresh did not result in a valid access token' };
            console.debug('[AuthService] Token refresh result:', status.status);

            this.refreshStatus.next(status);
            return status;
        } catch (error) {
            const status: RefreshStatus = { status: 'FAILED', error };
            console.debug('[AuthService] Token refresh failed:', error);

            this.refreshStatus.next(status);
            return status;
        }
    }

    public logout(): void {
        this.validateInit();
        this.localStorageService.removeItem(IDP_HINT_KEY);
        this.oauthService.logOut();
    }

    public isAuthenticated(): boolean {
        this.validateInit();
        return this.oauthService.hasValidAccessToken();
    }

    public getToken(): string {
        return this.oauthService.getAccessToken();
    }

    public getIdentityClaims(): object {
        return this.oauthService.getIdentityClaims() || {};
    }

    public getDomainWhitelist(): Array<string> {
        return this.authDomainWhitelist;
    }

    public getIsInitialized(): boolean {
        return this.isInitialized;
    }

    /**
     * Extra parameters to send along with the authorization request. `kc_idp_hint` makes Keycloak
     * skip its own login page and redirect straight to the named identity provider, so a user
     * arriving from an external portal never sees the email prompt.
     */
    private getAuthorizationParams(): Record<string, string> {
        const idpHint = this.localStorageService.getItem<string>(IDP_HINT_KEY);

        return idpHint ? { [IDP_HINT_PARAM]: idpHint } : {};
    }

    /**
     * Stores the `kc_idp_hint` query parameter on first sight, so later logins (e.g. the auth guard
     * redirecting after session expiry) keep skipping the Keycloak email prompt. Cleared on logout.
     */
    private persistIdpHintFromUrl(): void {
        if (!this.isBrowser) {
            return;
        }

        const idpHint = new URLSearchParams(window.location.search).get(IDP_HINT_PARAM);
        if (idpHint) {
            this.localStorageService.setItem(IDP_HINT_KEY, idpHint);
        }
    }

    private validateLogin(): Observable<boolean> {
        this.validateInit();
        if (this.isBrowser) {
            return from(this.oauthService.tryLogin());
        }
        return of(false);
    }

    private validateInit(): void {
        if (!this.isInitialized) {
            throw new Error('AuthService not initialized');
        }
    }
}

export function authCodeFlowConfig(config: HeaderConfig): AuthConfig {
    return {
        // Url of the Identity Provider
        issuer: `${config.keycloakUrl}/realms/${config.keycloakRealm}`,

        // URL of the SPA to redirect the user to after login
        redirectUri: config.redirectUri,

        // The SPA's id. The SPA is registerd with this id at the auth-server
        // clientId: 'server.code',
        clientId: config.keycloakClientId,

        // set the scope for the permissions the client should request
        // The first four are defined by OIDC.
        // Important: Request offline_access to get a refresh token
        // The api scope is a usecase specific one
        scope: config.keycloakScope,

        // Just needed if your auth server demands a secret. In general, this
        // is a sign that the auth server is not configured with SPAs in mind
        // and it might not enforce further best practices vital for security
        // such applications.
        // dummyClientSecret: 'secret',
        responseType: 'code',

        showDebugInformation: true,
    };
}
